/**
 * CSRF token cookie management.
 *
 * The backend (I03-2) sets a `__Host-aire-csrf` cookie: `Secure`, `SameSite=Strict`, `Path=/`,
 * no `Domain`, and `HttpOnly=false` so this JS can read it. On every state-changing request the
 * client echoes the value back in the `X-AIRE-CSRF-Token` header. A cross-origin script cannot
 * read the cookie (it is same-site only and the `__Host-` prefix forbids a `Domain`), so the
 * header can only be set by first-party code.
 *
 * See docs/01-architecture/frontend-security.md §4 and §7.
 */

export const CSRF_COOKIE_NAME = "__Host-aire-csrf";
export const CSRF_HEADER_NAME = "X-AIRE-CSRF-Token";

/**
 * HTTP methods that mutate state and therefore must carry the CSRF header. GETs/HEADs are
 * read-only by contract (frontend-security.md §4.1) and never carry it.
 */
const STATE_CHANGING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export function isStateChangingMethod(method: string): boolean {
  return STATE_CHANGING_METHODS.has(method.toUpperCase());
}

/**
 * Reads a cookie value from `document.cookie`. Returns `undefined` if absent. Tokens are never
 * read from `localStorage`/`sessionStorage` (frontend-security.md §5.1) — only this cookie.
 */
export function readCookie(name: string, cookieSource = globalThis.document.cookie): string | undefined {
  const target = `${name}=`;
  for (const part of cookieSource.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length));
    }
  }
  return undefined;
}

export function readCsrfToken(cookieSource?: string): string | undefined {
  return cookieSource === undefined ? readCookie(CSRF_COOKIE_NAME) : readCookie(CSRF_COOKIE_NAME, cookieSource);
}
